/**
 * useGeoWatch — the ONE live position source of a capture (US-03482 iPhone black-video RCA, G2, 2026-11-03).
 *
 * One `navigator.geolocation.watchPosition`, owned by CaptureFlow for the whole capture and shared by every step
 * (capture/GeoWatchContext.tsx): the LocationGate, the visit-level G1 row (media/useGeoEvidence.ts, watch mode) and the
 * stamp preview card all read it. Chrome on iOS (WKWebView) re-prompts on every new position acquisition, so the old
 * per-shot watch (and the per-shot `getCurrentPosition`) put a location prompt on every step — while the camera was
 * starting. Every callback goes through `rememberFix()`, so the card and the X-SV-Geo header read ONE source of truth
 * (`shotGeo.latestFix()`); `beginShot` still freezes whatever is held at that instant onto every chunk of a shot.
 *
 * An error never touches the remembered fix: the card keeps the last fix with its TRUE age (`collectedAt`) and says
 * why it is not refreshing. A watch that never calls back at all is answered by a watchdog (GEO_TIMEOUT_MS + grace)
 * as `unavailable` — nothing waits on a silent browser forever. `restart()` clears the watch and opens a new one (Try
 * again, a return from the phone's settings, a permission change), optionally at low accuracy.
 *
 * §16i, carried over from the probes this watch replaced (release audit, 2026-11-03):
 *   • while the site permission reads `prompt` the browser's own question may be on screen, and a watch's `timeout` does
 *     not run until it is answered (Geolocation spec) — so the watchdog waits GEO_PROMPT_CEILING_MS instead, and if the
 *     question may STILL be up then, the watch reports `asking` (no error: nothing is failed, no ERROR row is written)
 *     and keeps watching; the customer's answer arrives on the same watch (a fix, or code 1);
 *   • each (re)start is its own probe: a callback from a watch that a restart (or a stop) cleared is ignored, so a late
 *     answer from an older watch can neither set the new one's error nor end its `pending`.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { GEO_PROMPT_CEILING_MS, GEO_TIMEOUT_MS, GEO_WATCHDOG_GRACE_MS, permissionSoon } from '@/media/useGeoEvidence';
import { fixFromPosition, rememberFix } from './shotGeo';

export type GeoWatchError = 'denied' | 'unavailable' | 'timeout' | 'unsupported';

export interface GeoWatchState {
  /** True while a watch is registered. */
  readonly watching: boolean;
  /** The last error the watch reported, cleared by the next successful fix. */
  readonly error: GeoWatchError | null;
  /** True from a (re)start until the watch's first answer (a fix, an error, or the watchdog). */
  readonly pending: boolean;
  /**
   * §16i: the watchdog passed while the browser's permission question may still be on screen (`prompt`) — not a failure.
   * The watch keeps running; cleared by its next answer or a restart.
   */
  readonly asking: boolean;
  /** Clear the watch and open a new one; `highAccuracy` false asks for the network position too. */
  readonly restart: (opts?: { highAccuracy?: boolean }) => void;
  /** Whether the running watch asks for high accuracy (a low one is a fallback, put back once a fix exists). */
  readonly highAccuracy: boolean;
}

export const WATCH_OPTIONS: PositionOptions = { enableHighAccuracy: true, maximumAge: 5_000, timeout: GEO_TIMEOUT_MS };

const PERMISSION_DENIED = 1;
const TIMEOUT = 3;

export function useGeoWatch(enabled: boolean): GeoWatchState {
  const [error, setError] = useState<GeoWatchError | null>(null);
  const [watching, setWatching] = useState(false);
  const [pending, setPending] = useState(false);
  const [asking, setAsking] = useState(false);
  const [gen, setGen] = useState(0);
  const highAccuracy = useRef(true);
  const [high, setHigh] = useState(true);

  useEffect(() => {
    if (!enabled) return undefined;
    if (typeof navigator === 'undefined' || !navigator.geolocation || typeof navigator.geolocation.watchPosition !== 'function') {
      setError('unsupported');
      return undefined;
    }
    let id: number | null = null;
    let answered = false;
    // This watch's own life: a restart or a stop ends it, and its late callbacks are then ignored (probe ids, §16i).
    let live = true;
    setPending(true);
    setAsking(false);
    const answer = (): void => {
      answered = true;
      setPending(false);
      setAsking(false);
    };
    const startedAt = Date.now();
    // The permission state as last read for this watch: `prompt` = the browser's question may be on screen.
    let state: PermissionState | null = null;
    const unavailable = (): void => {
      answer();
      setError('unavailable');
    };
    // The watchdog: a watch that has said nothing at all by now is treated as "unavailable" (it keeps watching) — unless
    // the question may still be up, which is "asking", never a failure.
    const fire = (): void => {
      if (!live || answered) return;
      if (state !== 'prompt') {
        unavailable();
        return;
      }
      void permissionSoon().then((now) => {
        if (!live || answered) return;
        if (now !== 'prompt') {
          unavailable();
          return;
        }
        setPending(false);
        setAsking(true);
      });
    };
    let watchdog = window.setTimeout(fire, GEO_TIMEOUT_MS + GEO_WATCHDOG_GRACE_MS);
    void permissionSoon().then((now) => {
      if (!live || answered) return;
      state = now;
      if (now !== 'prompt') return;
      window.clearTimeout(watchdog);
      watchdog = window.setTimeout(fire, Math.max(0, startedAt + GEO_PROMPT_CEILING_MS - Date.now()));
    });
    try {
      id = navigator.geolocation.watchPosition(
        (pos) => {
          if (!live) return;
          answer();
          rememberFix(fixFromPosition(pos));
          setError(null);
        },
        (err) => {
          if (!live) return;
          answer();
          setError(err.code === PERMISSION_DENIED ? 'denied' : err.code === TIMEOUT ? 'timeout' : 'unavailable');
        },
        { ...WATCH_OPTIONS, enableHighAccuracy: highAccuracy.current },
      );
      setWatching(true);
    } catch {
      unavailable();
    }
    return () => {
      live = false;
      window.clearTimeout(watchdog);
      if (id !== null && typeof navigator.geolocation?.clearWatch === 'function') navigator.geolocation.clearWatch(id);
      setWatching(false);
      setPending(false);
      setAsking(false);
    };
  }, [enabled, gen]);

  const restart = useCallback((opts?: { highAccuracy?: boolean }): void => {
    highAccuracy.current = opts?.highAccuracy ?? true;
    setHigh(highAccuracy.current);
    setGen((g) => g + 1);
  }, []);

  return { watching: enabled && watching, error, pending: enabled && pending, asking: enabled && asking, restart, highAccuracy: high };
}
